import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logoUrl } from '../../data/mock';
import { Button } from '../ui/button';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, UserPlus, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Simplified nav links
const navLinks = [
  { name: "Home", path: "/" },
  { name: "Courses", path: "/courses" },
  { name: "Mock Exams", path: "/mock-exams" },
  { name: "OLQ Tracker", path: "/olq-tracker" },
  { name: "Contact", path: "/contact" }
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout, login } = useAuth();

  const isAdmin = user?.email?.toLowerCase() === 'info@jaydefenceacademy.com';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-navy-900/95 backdrop-blur-md border-b border-gold-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <img 
              src={logoUrl} 
              alt="Jay Defence Academy" 
              className="h-10 lg:h-12 w-auto object-contain"
            />
            <div className="hidden md:flex flex-col">
              <span className="text-sm lg:text-base font-bold text-white leading-tight">Jay Defence Academy</span>
              <span className="text-xs text-gold-400">Dharwad</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-gold-400 bg-gold-500/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* More dropdown for less important links */}
            <div className="relative">
              <button
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                More
                <ChevronDown className="w-4 h-4" />
              </button>
              {moreMenuOpen && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-xl border py-1 z-50">
                  <Link 
                    to="/notifications" 
                    onClick={() => setMoreMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-cream-50"
                  >
                    Notifications
                  </Link>
                  <Link 
                    to="/about-dharwad" 
                    onClick={() => setMoreMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-cream-50"
                  >
                    About Dharwad
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center text-navy-900 font-bold text-sm">
                      {user.name?.[0] || 'U'}
                    </div>
                  )}
                  <span className="text-white text-sm font-medium hidden xl:block">{user.name?.split(' ')[0]}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <Link 
                      to="/dashboard" 
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-cream-50 transition-colors"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      OLQ Dashboard
                    </Link>
                    {isAdmin && (
                      <Link 
                        to="/admin" 
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50 transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    )}
                    <hr className="my-1 border-gray-100" />
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={login}
                  className="px-3 py-2 text-sm font-medium text-white hover:text-gold-400 transition-colors"
                >
                  Login
                </button>
                <Button 
                  onClick={login}
                  className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-4 text-sm"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Sign Up Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-gray-300 hover:text-white transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10 animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                    location.pathname === link.path
                      ? 'text-gold-400 bg-gold-500/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/notifications"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg"
              >
                Notifications
              </Link>
              <Link
                to="/about-dharwad"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg"
              >
                About Dharwad
              </Link>
              
              <div className="border-t border-white/10 mt-2 pt-2">
                {user ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gold-400 bg-gold-500/10 rounded-lg"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      OLQ Dashboard
                    </Link>
                    {isAdmin && (
                      <Link 
                        to="/admin" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-purple-400 bg-purple-500/10 rounded-lg mt-1"
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg mt-1"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => { login(); setMobileMenuOpen(false); }}
                      className="w-full px-4 py-3 text-sm font-medium text-white hover:bg-white/5 rounded-lg text-left"
                    >
                      Login
                    </button>
                    <Button 
                      onClick={() => { login(); setMobileMenuOpen(false); }}
                      className="w-full mt-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Sign Up Free
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
